/**
 * scroll-children-gsap.js
 *
 * Passive module:
 * - Does NOT load GSAP by itself.
 * - Does NOT auto-scan the whole page.
 * - h6-animation-loader.js loads dependencies first, then calls init().
 *
 * Required marker:
 *   [data-apple-scroll-reveal]
 *
 * Stable reverse version:
 * - 支持向上滚动 reverse
 * - 支持 start/end 区域内快速反复滚动
 * - 避免 timeline reverse 后再次向下时卡在 opacity: 0
 * - Lazy Loader 初始化较晚时自动同步当前可见状态
 */
(function () {
  "use strict";

  if (window.H6ScrollChildrenAnimation) {
    return;
  }

  const instances = new Map();

  function deps() {
    if (!window.gsap || !window.ScrollTrigger) {
      return null;
    }

    window.gsap.registerPlugin(window.ScrollTrigger);

    return {
      gsap: window.gsap,
      ScrollTrigger: window.ScrollTrigger,
    };
  }

  function getNumber(value, fallback) {
    const number = parseFloat(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function getItems(container) {
    const selector = container.dataset.appleScrollItem;

    if (selector) {
      try {
        return Array.from(container.querySelectorAll(selector));
      } catch (error) {
        console.warn(
          "[H6 Scroll Children] 无效的 data-apple-scroll-item:",
          selector,
        );
      }
    }

    return Array.from(container.children);
  }

  function createAnimation(container) {
    if (!container) {
      return null;
    }

    const d = deps();

    if (!d) {
      console.warn("[H6 Scroll Children] GSAP / ScrollTrigger 尚未准备好");
      return null;
    }

    const gsap = d.gsap;
    const ScrollTrigger = d.ScrollTrigger;

    if (instances.has(container)) {
      return instances.get(container);
    }

    const items = getItems(container);

    if (!items.length) {
      return null;
    }

    const reduceMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduceMotion) {
      gsap.set(items, {
        y: 0,
        scale: 1,
        autoAlpha: 1,
        clearProps: "transform,opacity,visibility,willChange",
      });

      const reducedInstance = {
        container,
        items,
        timeline: null,
        trigger: null,
      };

      instances.set(container, reducedInstance);
      container.dataset.appleScrollInitialized = "true";

      return reducedInstance;
    }

    const startY = getNumber(container.dataset.appleScrollY, 120);

    const startScale = getNumber(container.dataset.appleScrollScale, 0.94);

    const stagger = Math.max(
      0,
      getNumber(container.dataset.appleScrollStagger, 0.14),
    );

    const duration = Math.max(
      0.01,
      getNumber(container.dataset.appleScrollDuration, 1.2),
    );

    /*
     * Exact "enter viewport" default.
     * You can still override with data-apple-scroll-start.
     */
    const start = (container.dataset.appleScrollStart || "top bottom").trim();

    /*
     * 向上滚动时开始执行“收回”动画的位置。
     *
     * 例如：
     * data-apple-scroll-end="top 55%"
     *
     * 向下滚动：
     *   到 start -> play
     *
     * 向上滚动：
     *   回到 end -> reverse
     *
     * 这样 reverse 会在元素仍然可见时开始，
     * 不会等到元素已经退出屏幕底部才收回。
     */
    const end = (container.dataset.appleScrollEnd || "20% bottom").trim();

    gsap.set(items, {
      y: startY,
      scale: startScale,
      autoAlpha: 0,
      transformOrigin: "50% 50%",
      force3D: true,
      willChange: "transform, opacity",
    });

    const timeline = gsap.timeline({
      paused: true,
    });

    timeline.to(items, {
      y: 0,
      scale: 1,
      autoAlpha: 1,
      duration,
      ease: container.dataset.appleScrollEase || "power2.out",
      stagger: {
        each: stagger,
        from: "start",
      },
      force3D: true,
    });

    timeline.eventCallback("onComplete", function () {
      gsap.set(items, {
        willChange: "auto",
      });
    });

    timeline.eventCallback("onReverseComplete", function () {
      gsap.set(items, {
        willChange: "transform, opacity",
      });
    });

    /*
     * 不再使用 toggleActions 控制正反方向。
     *
     * 原因：
     * 当用户在 start / end 区域附近快速反复滚动时，
     * timeline 可能正在 reverse，
     * 但再次向下穿过 end 时旧配置不会重新 play，
     * 最终就会停在 opacity: 0，看起来像“动画失效”。
     *
     * 这里明确控制四个边界事件：
     *
     * 向下：
     *   onEnter -> play
     *   onLeave -> play
     *
     * 向上：
     *   onEnterBack -> reverse
     *   onLeaveBack -> reverse
     *
     * 所以无论用户多快、多少次反复滚动，
     * 动画方向都会与当前滚动方向重新同步。
     */
    function prepareItems() {
      gsap.set(items, {
        willChange: "transform, opacity",
      });
    }

    /*
     * 正向动画继续使用原来的 duration。
     *
     * 反向动画单独限制总时长，默认最多 0.4s。
     * 这里使用 timeScale，而不是直接修改 timeline 的 duration，
     * 这样可以把 stagger 也一起压缩到 0.4s 内完成。
     *
     * 可选覆盖：
     * data-apple-scroll-reverse-duration="0.4"
     */
    const reverseDuration = Math.max(
      0.05,
      getNumber(container.dataset.appleScrollReverseDuration, 0.4),
    );

    function playForward() {
      prepareItems();

      // 从反向重新切回正向时，恢复正常播放速度。
      timeline.timeScale(1);
      timeline.play();
    }

    function playBackward() {
      prepareItems();

      const totalDuration = Math.max(0.001, timeline.duration());

      // 保证完整 reverse（包括 stagger）最多在 reverseDuration 内结束。
      timeline.timeScale(Math.max(1, totalDuration / reverseDuration));

      timeline.reverse();
    }

    const trigger = ScrollTrigger.create({
      trigger: container,

      start,
      end,

      onEnter: playForward,
      onLeave: playForward,

      onEnterBack: playBackward,
      onLeaveBack: playBackward,

      invalidateOnRefresh: true,
      markers: false,
    });

    /*
     * Lazy Loader 可能在元素已经进入/越过触发区域之后
     * 才真正初始化本模块。
     *
     * 初始化后同步一次可见状态，避免：
     * 页面已经滚过该区域，但 item 仍停留在 opacity: 0。
     */
    requestAnimationFrame(function () {
      trigger.refresh();

      if (trigger.progress >= 1) {
        timeline.progress(1).pause();

        gsap.set(items, {
          y: 0,
          scale: 1,
          autoAlpha: 1,
          willChange: "auto",
        });

        return;
      }

      if (trigger.progress <= 0) {
        timeline.progress(0).pause();

        gsap.set(items, {
          y: startY,
          scale: startScale,
          autoAlpha: 0,
          willChange: "transform, opacity",
        });

        return;
      }

      /*
       * 初始化时刚好位于 start/end 中间：
       * 保证 item 不会卡死在隐藏状态。
       */
      if (trigger.direction < 0) {
        timeline.progress(1).pause();
        playBackward();
      } else {
        playForward();
      }
    });

    const instance = {
      container,
      items,
      timeline,
      trigger,
    };

    instances.set(container, instance);
    container.dataset.appleScrollInitialized = "true";

    return instance;
  }

  function init(root) {
    const d = deps();

    if (!d) {
      return false;
    }

    root = root || document;

    const containers = [];

    if (root.matches && root.matches("[data-apple-scroll-reveal]")) {
      containers.push(root);
    }

    if (root.querySelectorAll) {
      containers.push(...root.querySelectorAll("[data-apple-scroll-reveal]"));
    }

    containers.forEach(createAnimation);

    requestAnimationFrame(function () {
      d.ScrollTrigger.refresh();
    });

    return true;
  }

  function destroy(root) {
    const d = deps();

    instances.forEach(function (instance, container) {
      const shouldDestroy =
        container === root ||
        (root && root.contains && root.contains(container));

      if (!shouldDestroy) {
        return;
      }

      if (instance.trigger) {
        instance.trigger.kill();
      }

      if (instance.timeline) {
        instance.timeline.kill();
      }

      if (d && instance.items.length) {
        d.gsap.set(instance.items, {
          clearProps: "transform,opacity,visibility,willChange",
        });
      }

      delete container.dataset.appleScrollInitialized;
      instances.delete(container);
    });
  }

  window.H6ScrollChildrenAnimation = {
    init,
    create: createAnimation,
    destroy,
    refresh: function () {
      const d = deps();
      if (d) {
        d.ScrollTrigger.refresh();
      }
    },
    instances,
  };
})();
